import type { PolygonClosedContour } from '../polygon-closed-contour';
import type { PolygonOpenContour } from '../polygon-open-contour';
import type { PointArray } from '../types/contour-definition-array-type';

import { getClosestPointIdx } from './getClosestPointIdx';
import { interpolateGridPoints2D } from './interpolatePolygonToGrid';
import { removeLoops } from './removeLoops';
import { snapPolyLineToSubPixelGrid } from './snapPolyLineToSubPixelGrid';

export function createSaxOpenEpiMyocardialPolygon(
  endoContour: PolygonClosedContour,
  openEpiContour: PolygonOpenContour,
): PointArray {
  const endo = endoContour.getPolygon();
  const epi = openEpiContour.getPolygon();

  if (epi.length < 2 || endo.length < 3) {
    return [];
  }

  const epiStart = epi[0];
  const epiEnd = epi[epi.length - 1];

  // closest endo points to the two open-epi ends
  const idxStart = getClosestPointIdx(epiStart, endo); // anchor for epi[0]
  const idxEnd = getClosestPointIdx(epiEnd, endo); // anchor for epi[last]

  // bridge epi[last] -> endo[idxEnd]
  let endConnection: PointArray = [epiEnd, endo[idxEnd]];
  endConnection = interpolateGridPoints2D(endConnection, 1);
  endConnection = snapPolyLineToSubPixelGrid(endConnection, false);

  // bridge endo[idxStart] -> epi[0]
  let startConnection: PointArray = [endo[idxStart], epiStart];
  startConnection = interpolateGridPoints2D(startConnection, 1);
  startConnection = snapPolyLineToSubPixelGrid(startConnection, false);

  // collect the endo points strictly between idxEnd and idxStart for a step dir
  const collectArc = (dir: number): PointArray => {
    const arc: PointArray = [];
    for (
      let i = (idxEnd + dir + endo.length) % endo.length;
      i !== idxStart;
      i = (i + dir + endo.length) % endo.length
    ) {
      arc.push(endo[i]);
      if (arc.length >= endo.length) break; // safety (idxStart === idxEnd)
    }
    return arc;
  };

  const arcCW = collectArc(1);
  const arcCCW = collectArc(-1);

  // choose the endo arc that lies under the epi arc (nearest midpoints)
  const epiMid = epi[Math.floor(epi.length / 2)];
  const midDistSq = (arc: PointArray): number => {
    if (arc.length === 0) return Number.POSITIVE_INFINITY;
    const m = arc[Math.floor(arc.length / 2)];
    const dx = m.x - epiMid.x;
    const dy = m.y - epiMid.y;
    return dx * dx + dy * dy;
  };
  const endoArc = midDistSq(arcCW) <= midDistSq(arcCCW) ? arcCW : arcCCW;

  // assemble: epi arc -> bridge -> endo arc -> bridge back -> close
  let fusion: PointArray = [];
  for (const p of epi) fusion.push({ x: p.x, y: p.y });
  // drop first point of endConnection (duplicates epiEnd), keep endo[idxEnd]
  for (let i = 1; i < endConnection.length; i++) {
    fusion.push({ x: endConnection[i].x, y: endConnection[i].y });
  }
  for (const p of endoArc) fusion.push({ x: p.x, y: p.y });
  // drop last point of startConnection (duplicates epiStart); close adds it back
  for (let i = 0; i < startConnection.length - 1; i++) {
    fusion.push({ x: startConnection[i].x, y: startConnection[i].y });
  }
  // remove duplicate points (keep first occurrence), then re-close
  const seen = new Set<string>();
  fusion = fusion.filter((p) => {
    const key = `${p.x},${p.y}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  if (
    fusion[0].x !== fusion[fusion.length - 1].x
    || fusion[0].y !== fusion[fusion.length - 1].y
  ) {
    fusion.push({ x: fusion[0].x, y: fusion[0].y });
  }
  fusion = removeLoops(fusion);
  return fusion;
}
